import type { Record } from './streak';
import { streakBarPct, streakColour, streakFlames } from './streak';
import { formatCents } from '@/utils/money';

/*
  The prototype's win-streak meter, with the win/loss record underneath — the
  stakeholder's preferred place to manage bet history (2026-10-13). Draws
  `computeRecord()`'s output and nothing else.
*/
export function StreakMeter({ record }: { record: Record }) {
  const s = record.currentStreak;
  const flames = streakFlames(s);
  const sub = record.settled === 0
    ? 'Your first settled bet starts the meter.'
    : s === 0
      ? 'Streak reset — the next win starts a new run.'
      : `${s} win${s === 1 ? '' : 's'} in a row${s >= 3 ? " — you're on fire!" : ' — keep it up!'}`;
  return (
    <div className="rounded-2xl border border-white/[0.08] bg-brand-surface/80 p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-display font-bold text-[15px]">Win streak {flames}</div>
          <div className="text-xs text-white/45 mt-0.5">{sub}</div>
        </div>
        <div className="text-4xl leading-none" aria-hidden="true">{s >= 3 ? flames : '🎯'}</div>
      </div>
      <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${streakBarPct(s)}%`, background: streakColour(s) }} />
      </div>
      <div className="flex justify-between text-[10px] text-white/30 mt-1">
        <span>0</span><span>🔥 3</span><span>🔥🔥 5</span><span>🔥🔥🔥 8+</span>
      </div>
      <div className="mt-4 grid grid-cols-4 gap-2 text-center">
        <Stat label="Won" value={String(record.won)} cls="text-brand-green" />
        <Stat label="Lost" value={String(record.lost)} cls="text-red-400" />
        <Stat label="Strike rate" value={record.won + record.lost === 0 ? '—' : `${Math.round(record.strikeRate * 100)}%`} />
        <Stat label="Best run" value={String(record.bestStreak)} />
      </div>
      {record.settled > 0 && (
        <div className="mt-3 flex items-center justify-between text-xs border-t border-white/[0.07] pt-3">
          <span className="text-white/45">Net on settled bets</span>
          <span className={`font-semibold tabular-nums ${record.netCents >= 0 ? 'text-brand-green' : 'text-red-400'}`}>
            {record.netCents >= 0 ? '+' : '−'}{formatCents(Math.abs(record.netCents))}
          </span>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, cls = '' }: { label: string; value: string; cls?: string }) {
  return (
    <div className="rounded-xl bg-brand-navy2/70 border border-white/[0.06] py-2">
      <div className={`font-display text-lg font-extrabold tabular-nums ${cls}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-[0.06em] text-white/40">{label}</div>
    </div>
  );
}
